/**
 * Logique partagée pour GET /api/statistiques/rapport-stock
 * (RapportStockParType, récap du RapportActivite).
 */

export function extractRapportRoot(response) {
  const body = response && response.data !== undefined ? response.data : response;
  if (!body || typeof body !== "object") return null;
  if (body.data !== undefined && body.data !== null && typeof body.data === "object") {
    return body.data;
  }
  return body;
}

export function normalizeTypePeriode(period) {
  if (period === "MENSUEL") return "MENSUELLE";
  return period;
}

export function buildRapportStockUrl(filters, period) {
  const url = `/api/statistiques/rapport-stock`;
  const params = new URLSearchParams();
  const f = filters || {};

  if (period) {
    params.append("type_periode", normalizeTypePeriode(String(period)));
  }
  if (f.period_from) {
    params.append("period_from", String(f.period_from).slice(0, 10));
  }
  if (f.period_to) {
    params.append("period_to", String(f.period_to).slice(0, 10));
  }

  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export function resolvePeriodes(root) {
  if (!root || typeof root !== "object") return [];
  if (Array.isArray(root.periodes) && root.periodes.length) {
    return root.periodes;
  }
  const lignes = root.lignes ?? root.rows ?? root.lines;
  if (Array.isArray(lignes) && lignes.length) {
    return [
      {
        periode: root.au ?? root.period_to ?? root.period_from,
        period_from: root.period_from,
        period_to: root.period_to,
        nombre_produits_considere: root.nombre_produits_considere,
        lignes,
      },
    ];
  }
  return [];
}

export function extractLignesBrutes(root) {
  const periodes = resolvePeriodes(root);
  if (!periodes.length) return [];

  const byCategorie = new Map();
  const sumKeys = [
    "prix_de_vente",
    "ht",
    "prix_achat",
    "prix_achat_ht",
    "prix_achat_selon_coef_ttc",
    "prix_achat_selon_coef_ht",
  ];

  // Always aggregate by category, whether single or multiple periods
  // This ensures all products within the same category are summed for correct totals
  periodes.forEach((periode) => {
    const lignes = Array.isArray(periode.lignes) ? periode.lignes : [];
    lignes.forEach((row) => {
      const key =
        row.famille ??
        row.famille_libelle ??
        row.categorie_du_produit ??
        row.type_actuel ??
        row.type ??
        row.rayon_du_produit ??
        "—";
      if (!byCategorie.has(key)) {
        byCategorie.set(key, { ...row, categorie_du_produit: key });
        return;
      }
      const acc = byCategorie.get(key);
      sumKeys.forEach((k) => {
        acc[k] = (Number(acc[k]) || 0) + (Number(row[k]) || 0);
      });
    });
  });

  return Array.from(byCategorie.values());
}

export function normalizeStockRow(raw) {
  const tvaApplicable =
    raw.tva_du_produit !== undefined
      ? Boolean(raw.tva_du_produit)
      : raw.tva_applicable !== undefined
        ? Boolean(raw.tva_applicable)
        : raw.tva_pct != null
          ? Number(raw.tva_pct) > 0
          : null;

  return {
    type_actuel:
      raw.famille ??
      raw.famille_libelle ??
      raw.categorie_du_produit ??
      raw.type_actuel ??
      raw.Type_actuel ??
      raw.type ??
      "—",
    marge_coef:
      raw.coef_conversion_de_prix_vente_achat ??
      raw.marge_coef ??
      raw.marge_pct ??
      raw.marge ??
      raw.Marge,
    tva_applicable: tvaApplicable,
    prix_public_ttc: raw.prix_de_vente ?? raw.prix_public_ttc ?? raw.Prix_PUBLIC_TTC,
    prix_vente_ht: raw.ht ?? raw.prix_vente_ht ?? raw.Prix_Vente_HT,
    prix_achat_ttc: raw.prix_achat ?? raw.prix_achat_ttc ?? raw.Prix_Achat_TTC,
    prix_achat_ht: raw.prix_achat_ht ?? raw.Prix_Achat_HT,
    prix_achat_selon_marge_ttc:
      raw.prix_achat_selon_coef_ttc ??
      raw.prix_achat_selon_marge_ttc ??
      raw.Prix_Achat_selon_Marge_TTC,
    prix_achat_selon_marge_ht:
      raw.prix_achat_selon_coef_ht ??
      raw.prix_achat_selon_marge_ht ??
      raw.Prix_Achat_selon_Marge_HT,
  };
}

export function normalizeRecapType(type) {
  if (type == null) return "";
  return String(type)
    .trim()
    .toLocaleLowerCase("fr")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9 ]+/g, "")
    .replace(/\s+/g, " ");
}

export function overrideMargeParType(type, marge) {
  const key = normalizeRecapType(type);
  if (key === "laitinfantil" || key === "lait infantil") return 1.15;
  if (key === "preservatif") return 1.64;
  if (key === "parapharmacie") return 1.64;
  if (key === "medicaments" || key === "medicament") return 1.33;
  return marge;
}

export function lignesBrutesToRecapActivite(lignesBrutes) {
  return lignesBrutes.map((row) => {
    const normalized = normalizeStockRow(row);
    return {
      type_actuel: normalized.type_actuel,
      taux_marge: overrideMargeParType(normalized.type_actuel, normalized.marge_coef),
      prix_public_ttc: normalized.prix_public_ttc,
    };
  });
}

export function computeRecapActiviteTotaux(lignes) {
  const prixPublic = lignes.reduce(
    (acc, row) => acc + (Number(row.prix_public_ttc) || 0),
    0
  );
  return {
    prix_public_ttc: prixPublic,
    pp_bl_avoirs: 0,
    pph_bl_avoirs: 0,
  };
}
